import React, { useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTestNavigation } from "../../../utils/testNavigation";
import { useGetTestAttemptAllResultsQuery } from "../mockTestSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HearingIcon from "@mui/icons-material/Hearing";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CreateIcon from "@mui/icons-material/Create";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GradeIcon from "@mui/icons-material/Grade";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import QuizIcon from "@mui/icons-material/Quiz";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ListeningSectionReview from "./ListeningSectionReview";
import ReadingSectionReview from "./ReadingSectionReview";
import WritingSectionReview from "./WritingSectionReview";
import SpeakingSectionReview from "./SpeakingSectionReview";
import HtmlRenderer from "./HtmlRenderer";
import { generateExplanationWithRetry } from "../../../utils/openaiService";

// Brand colors
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";

const moduleConfig = {
  listening: {
    key: "listeningResult",
    name: "Listening",
    icon: HearingIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
  reading: {
    key: "readingResult",
    name: "Reading",
    icon: MenuBookIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
  writing: {
    key: "writingResult",
    name: "Writing",
    icon: CreateIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
  speaking: {
    key: "speakingResult",
    name: "Speaking",
    icon: RecordVoiceOverIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
};

// Helper function to clean question text
const cleanQuestionText = (raw = "") => String(raw).replace(/\s+/g, " ").trim();

// Helper function to normalize heading
const normalizeHeading = (raw) => {
  if (!raw) return "";
  // Remove HTML tags
  const withoutTags = String(raw).replace(/<[^>]*>/g, "");
  const parts = withoutTags
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts
    .join(" – ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

// Component to render fill-in-blank question with answer display
const FillInBlankQuestion = ({
  question,
  userAnswer,
  config,
  questionNumber,
}) => {
  const isAttempted = !!userAnswer;
  const isCorrect = userAnswer?.isCorrect;

  // Replace blanks with answer display
  const renderQuestionWithAnswer = () => {
    let questionText = question.question || "";

    // Find the blank pattern (numbers in parentheses followed by underscores)
    const blankPattern = /\((\d+)\)_+/g;

    if (blankPattern.test(questionText)) {
      // Reset the regex
      blankPattern.lastIndex = 0;

      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = blankPattern.exec(questionText)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(
            <HtmlRenderer
              key={`text-${lastIndex}`}
              content={questionText.slice(lastIndex, match.index)}
              component="span"
            />,
          );
        }

        // Add the answer display
        parts.push(
          <span
            key={`answer-${match.index}`}
            style={{
              fontWeight: "600",
              padding: "2px 8px",
              borderRadius: "4px",
              backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
              color: isCorrect ? "#166534" : "#991b1b",
              margin: "0 4px",
              display: "inline-block",
            }}
          >
            ({match[1]}) {userAnswer?.givenAnswer || "___"}
          </span>,
        );

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < questionText.length) {
        parts.push(
          <HtmlRenderer
            key={`text-end`}
            content={questionText.slice(lastIndex)}
            component="span"
          />,
        );
      }

      return parts;
    }

    // If no blank pattern, just return the question with HTML
    return <HtmlRenderer content={questionText} component="span" />;
  };

  return (
    <div
      style={{
        marginBottom: "16px",
        padding: "16px",
        borderRadius: "10px",
        backgroundColor: !isAttempted
          ? "#f8fafc"
          : isCorrect
            ? "#f0fdf4"
            : "#fef2f2",
        border: `1px solid ${
          !isAttempted ? "#e2e8f0" : isCorrect ? "#86efac" : "#fecaca"
        }`,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "28px",
            height: "28px",
            borderRadius: "50%",
            backgroundColor: config.color,
            color: "white",
            fontSize: "12px",
            fontWeight: "600",
            flexShrink: 0,
          }}
        >
          {questionNumber || question.questionId}
        </span>
        <div style={{ flex: 1 }}>
          <div
            style={{ fontSize: "14px", color: "#1e293b", lineHeight: "1.6" }}
          >
            {renderQuestionWithAnswer()}
          </div>

          {isAttempted && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
                  border: `1px solid ${isCorrect ? "#86efac" : "#fecaca"}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                {isCorrect ? (
                  <CheckCircleIcon
                    style={{ fontSize: "16px", color: "#22c55e" }}
                  />
                ) : (
                  <CancelIcon style={{ fontSize: "16px", color: "#ef4444" }} />
                )}
                <span style={{ color: isCorrect ? "#166534" : "#991b1b" }}>
                  <strong>Your Answer:</strong> {userAnswer.givenAnswer}
                </span>
              </div>

              {!isCorrect && (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#dcfce7",
                    border: "1px solid #86efac",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <CheckCircleIcon
                    style={{ fontSize: "16px", color: "#22c55e" }}
                  />
                  <span style={{ color: "#166534" }}>
                    <strong>Correct Answer:</strong> {userAnswer.correctAnswer}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {isAttempted && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "16px",
              fontSize: "12px",
              fontWeight: "500",
              backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
              color: isCorrect ? "#166534" : "#991b1b",
              flexShrink: 0,
            }}
          >
            {isCorrect ? (
              <>
                <CheckCircleIcon style={{ fontSize: "14px" }} /> Correct
              </>
            ) : (
              <>
                <CancelIcon style={{ fontSize: "14px" }} /> Wrong
              </>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

// Component to render MCQ question with answer display
const MCQQuestion = ({ question, userAnswer, config }) => {
  const isAttempted = !!userAnswer;
  const isCorrect = userAnswer?.isCorrect;
  const options = question.options || [];

  // Parse user answers and correct answers for multiple selection
  const parseAnswers = (answerString) => {
    if (!answerString) return [];
    if (typeof answerString === "string") {
      return answerString
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    if (Array.isArray(answerString)) {
      return answerString.map((s) => String(s).trim().toLowerCase());
    }
    return [String(answerString).trim().toLowerCase()];
  };

  const userAnswers = parseAnswers(userAnswer?.givenAnswer);
  const correctAnswers = parseAnswers(
    userAnswer?.correctAnswer || question.correctOption,
  );

  return (
    <div
      style={{
        marginBottom: "20px",
        padding: "16px",
        borderRadius: "10px",
        backgroundColor: !isAttempted
          ? "#f8fafc"
          : isCorrect
            ? "#f0fdf4"
            : "#fef2f2",
        border: `1px solid ${
          !isAttempted ? "#e2e8f0" : isCorrect ? "#86efac" : "#fecaca"
        }`,
      }}
    >
      {/* Question Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "28px",
            height: "28px",
            borderRadius: "50%",
            backgroundColor: config.color,
            color: "white",
            fontSize: "12px",
            fontWeight: "600",
            flexShrink: 0,
          }}
        >
          Q{question.questionId}
        </span>
        <div style={{ flex: 1 }}>
          <HtmlRenderer
            content={question.question}
            component="p"
            sx={{
              margin: 0,
              fontSize: "14px",
              fontWeight: "500",
              color: "#1e293b",
            }}
          />
        </div>
        {isAttempted && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "16px",
              fontSize: "12px",
              fontWeight: "500",
              backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
              color: isCorrect ? "#166534" : "#991b1b",
              flexShrink: 0,
            }}
          >
            {isCorrect ? (
              <>
                <CheckCircleIcon style={{ fontSize: "14px" }} /> Correct
              </>
            ) : (
              <>
                <CancelIcon style={{ fontSize: "14px" }} /> Wrong
              </>
            )}
          </span>
        )}
      </div>

      {/* Options */}
      <div style={{ marginLeft: "40px" }}>
        {options.map((option, optIndex) => {
          const optionLabel =
            option.label?.toLowerCase() || String.fromCharCode(97 + optIndex);
          const rawText = option.text || option;
          const optionText =
            rawText === "true" ? "True" :
            rawText === "false" ? "False" :
            rawText === "not_given" ? "Not Given" :
            rawText === "yes" ? "Yes" :
            rawText === "no" ? "No" :
            rawText;

          const isUserAnswer = userAnswers.includes(optionLabel);
          const isCorrectOption = correctAnswers.includes(optionLabel);

          // For multiple selection, determine if this specific selected answer was right or wrong
          const isCorrectSelection = isUserAnswer && isCorrectOption;
          const isWrongSelection = isUserAnswer && !isCorrectOption;

          return (
            <div
              key={optIndex}
              style={{
                padding: "12px 16px",
                marginBottom: "8px",
                borderRadius: "8px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: isCorrectOption
                  ? "#dcfce7"
                  : isWrongSelection
                    ? "#fee2e2"
                    : "#fff",
                border: `2px solid ${
                  isCorrectOption
                    ? "#22c55e"
                    : isWrongSelection
                      ? "#ef4444"
                      : "#e2e8f0"
                }`,
                color: "#374151",
              }}
            >
              <span
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isCorrectOption
                    ? "#22c55e"
                    : isWrongSelection
                      ? "#ef4444"
                      : "#e2e8f0",
                  color:
                    isCorrectOption || isWrongSelection ? "white" : "#64748b",
                  fontSize: "13px",
                  fontWeight: "600",
                  flexShrink: 0,
                }}
              >
                {optionLabel.toUpperCase()}
              </span>
              <span
                style={{ flex: 1 }}
                dangerouslySetInnerHTML={{
                  __html: optionText.replace
                    ? optionText.replace(/<[^>]*>/g, "")
                    : optionText,
                }}
              />
              {isCorrectOption && (
                <CheckCircleIcon
                  style={{ fontSize: "20px", color: "#22c55e" }}
                />
              )}
              {isWrongSelection && (
                <CancelIcon style={{ fontSize: "20px", color: "#ef4444" }} />
              )}
              {isUserAnswer && (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    backgroundColor: isCorrectSelection ? "#166534" : "#991b1b",
                    color: "white",
                    fontWeight: "500",
                  }}
                >
                  Your Answer
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main component to render test content - detects test type and renders appropriate view
const TestContentView = ({
  moduleData,
  moduleConfig: config,
  onListeningExplainRequest,
  listeningLoadingExplanations,
  listeningExplanations,
  onReadingExplainRequest,
  readingLoadingExplanations,
  readingExplanations,
}) => {
  const test = moduleData.test;
  const answers = moduleData.answers || [];

  // Handle Writing module separately (no test structure, shows submission data)
  if (config.key === "writingResult") {
    // Task definitions from test (for left side - shows instructions and task prompts)
    const testTasks = moduleData.test?.tasks || [];

    const writingData = {
      sections: moduleData.test?.sections || [],
      writingPrompt: moduleData.test?.writingPrompt || "",
      instructions: moduleData.test?.instructions || "",
      title: moduleData.test?.title || "",
      test: moduleData.test,
      // Use test.tasks for task definitions (left side display)
      writingTasks: testTasks,
    };

    // Student submission tasks (for right side - shows submitted images/text)
    const submissionTasks = moduleData.writingTasks || [];

    // Aggregate images and text from all tasks
    let allWritingImages = [];
    let allWritingText = "";
    let isPictureBased = false;
    let checkingStatus = moduleData.checkingStatus || "";
    let overallGrade = moduleData.grade || "";

    if (submissionTasks.length > 0) {
      // Extract data from writingTasks array
      submissionTasks.forEach((task, index) => {
        if (task.writingImage && task.writingImage.length > 0) {
          allWritingImages = [...allWritingImages, ...task.writingImage];
          isPictureBased = true;
        }
        if (task.writingText) {
          allWritingText +=
            (index > 0 ? "\n\n--- Task " + (index + 1) + " ---\n\n" : "") +
            task.writingText;
        }
        // Use the first non-null checking status
        if (!checkingStatus && task.checkingStatus) {
          checkingStatus = task.checkingStatus;
        }
      });

      // Calculate overall grade from individual tasks if not available
      if (!overallGrade && submissionTasks.length > 0) {
        const taskGrades = submissionTasks
          .map((task) => parseFloat(task.grade))
          .filter((grade) => !isNaN(grade));

        if (taskGrades.length > 0) {
          // Calculate average and round to nearest 0.5
          const avgGrade =
            taskGrades.reduce((sum, grade) => sum + grade, 0) /
            taskGrades.length;
          overallGrade = (Math.round(avgGrade * 2) / 2).toFixed(1);
        }
      }

      // Determine overall checking status from tasks
      if (!checkingStatus) {
        const allChecked = submissionTasks.every(
          (task) => task.checkingStatus === "checked",
        );
        const anyPending = submissionTasks.some(
          (task) => task.checkingStatus === "pending",
        );
        checkingStatus = allChecked ? "checked" : anyPending ? "pending" : "";
      }
    }

    const submissionData = {
      _id: moduleData._id || moduleData.id || "", // Add the writing answer ID for comments
      writingText: allWritingText || moduleData.writingText || "",
      writingImage:
        allWritingImages.length > 0
          ? allWritingImages
          : moduleData.writingImage || [],
      isPictureBased: isPictureBased || moduleData.writingImage?.length > 0,
      evaluationPdf: moduleData.pdfUrl || "",
      marks: overallGrade,
      grade: overallGrade,
      adminComments: moduleData.accessorFeedBack || "",
      createdAt: moduleData.createdAt || "",
      checkingStatus: checkingStatus,
      evaluatorId: moduleData.evaluatorId || null,
      // Include submission tasks for right side display
      writingTasks: submissionTasks,
    };

    return (
      <WritingSectionReview
        writingData={writingData}
        submissionData={submissionData}
      />
    );
  }

  // Handle Speaking module separately
  if (config.key === "speakingResult") {
    const speakingData = {
      mainCards: moduleData.test?.mainCards || [],
      title: moduleData.test?.title || "",
      instructions: moduleData.test?.instructions || "",
      test: moduleData.test,
    };

    const submissionData = {
      _id: moduleData._id || null,
      speakingAnswerId: moduleData._id || null,
      speakingAudios: moduleData.speakingAudios || [],
      pdfUrl: moduleData.pdfUrl || "", // Pass the full pdfUrl object/string
      evaluationPdf:
        moduleData.pdfUrl?.pdfUrl ||
        moduleData.accessorFeedBackInPdf?.pdfUrl ||
        "",
      accessorFeedBackInPdf: moduleData.accessorFeedBackInPdf || null,
      marks: moduleData.score ?? moduleData.obtainedMarks,
      grade: moduleData.grade || "",
      adminComments: moduleData.accessorFeedBack || "",
      createdAt: moduleData.createdAt || "",
      checkingStatus: moduleData.checkingStatus || "",
      evaluatorId: moduleData.evaluatorId || null,
      evaluationType: moduleData.evaluationType || "",
      testData: moduleData.test || {},
    };

    return (
      <SpeakingSectionReview
        speakingData={speakingData}
        submissionData={submissionData}
      />
    );
  }

  if (!test) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
        <DescriptionIcon
          style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "16px" }}
        />
        <p>Test content not available for review</p>
      </div>
    );
  }

  // Transform answers to the format expected by review components
  const transformedAnswers = answers.map((a) => ({
    questionId: a.questionId,
    isCorrect: a.isCorrect,
    studentAnswer: a.givenAnswer || a.studentAnswer,
    correctAnswer: a.correctAnswer,
    explanation: a.explanation,
  }));

  // Detect if this is a listening test (has listeningParts, sections with audio, or parts with extracts structure)
  const isListeningTest =
    test.listeningParts ||
    (test.sections && test.sections.some((section) => section.audioUrl)) ||
    (test.parts &&
      test.parts.some((part) => part.extracts && part.extracts.length > 0));

  if (isListeningTest) {
    // Format listening data - handle both OET (parts) and IELTS (sections) formats
    const listeningData = test.sections
      ? { sections: test.sections }
      : test.parts
        ? { parts: test.parts }
        : test;

    return (
      <ListeningSectionReview
        listeningData={listeningData}
        answers={transformedAnswers}
        onExplainRequest={onListeningExplainRequest}
        loadingExplanations={listeningLoadingExplanations}
        explanations={listeningExplanations}
        moduleData={moduleData}
      />
    );
  }

  // Transform test data for ReadingSectionReview
  // For IELTS reading, questions need to be attached to parts
  let readingData = test;

  if (test.parts && test.parts.length > 0) {
    // IELTS format - parts have paragraph text but questions come from answers
    // Create a lookup map of answers by partName (sequence)
    const answersByPartIndex = {};
    answers.forEach((answer) => {
      if (!answersByPartIndex[answer.questionId]) {
        answersByPartIndex[answer.questionId] = answer;
      }
    });

    console.log("🔍 AttemptResultPage - answers:", answers);
    console.log("🔍 AttemptResultPage - test.parts:", test.parts);

    // Enhance parts with question information
    const enhancedParts = test.parts.map((part, idx) => {
      console.log(`\n📦 Processing Part ${idx}:`, {
        _id: part._id,
        partName: part.partName,
        questionsLength: part.questions?.length,
      });

      // Normalize common fields so the review UI can detect part label and heading
      const normalizedPart = {
        ...part,
        partName: part.partName || part.part || undefined,
        heading:
          part.heading || part.partHeading || part.textTitle || undefined,
        instructions: part.instructions || undefined,
        paragraph: part.paragraph || undefined,
      };

      // Determine a reliable part label (A, B, C) for descriptions and UI
      const partLabelFromOrder =
        typeof normalizedPart.order === "number" &&
        normalizedPart.order >= 1 &&
        normalizedPart.order <= 26
          ? String.fromCharCode(64 + normalizedPart.order) // 1 -> A
          : null;

      const partLabel =
        normalizedPart.part ||
        normalizedPart.partName ||
        partLabelFromOrder ||
        (idx < 3 ? ["A", "B", "C"][idx] : `P${idx + 1}`);

      normalizedPart.part = partLabel;

      // Prefer questions coming with the part (from test.parts[i].questions).
      // If missing, fall back to creating lightweight question objects from answers that match this part.
      const partQuestions =
        Array.isArray(normalizedPart.questions) &&
        normalizedPart.questions.length > 0
          ? normalizedPart.questions
          : []; // Don't create questions from answers if part already has questions array

      console.log(
        `  ✅ partQuestions length: ${partQuestions.length}`,
        partQuestions.map((q) => q.questionId),
      );

      console.log(
        `  ✅ partQuestions length: ${partQuestions.length}`,
        partQuestions.map((q) => q.questionId),
      );

      // If this part provides a single paragraph (`paragraph`) use it to build `texts` for the review UI
      // If texts already exist, use them; otherwise create from paragraph
      // Only create texts if there are questions to show
      const texts =
        Array.isArray(normalizedPart.texts) && normalizedPart.texts.length > 0
          ? normalizedPart.texts
          : normalizedPart.paragraph && partQuestions.length > 0
            ? [
                {
                  title:
                    normalizedPart.textTitle ||
                    normalizedPart.partHeading ||
                    normalizedPart.heading ||
                    `Passage ${idx + 1}`,
                  paragraph: normalizedPart.paragraph,
                  imageUrl: normalizedPart.imageUrl,
                  questions: partQuestions,
                },
              ]
            : [];

      console.log(`  📄 texts array length: ${texts.length}`);

      console.log(`  📄 texts array length: ${texts.length}`);

      // Ensure heading fallback is present so Title shows useful text
      const heading =
        normalizedPart.heading ||
        normalizedPart.partHeading ||
        `Passage ${idx + 1}`;

      // Only include questions at part level if there are no texts
      // This prevents duplication when questions are already in texts
      const finalPart = {
        ...normalizedPart,
        part: partLabel,
        heading,
        questions:
          texts.length === 0 && partQuestions.length > 0 ? partQuestions : [],
        texts,
      };

      console.log(`  📊 Final part:`, {
        part: finalPart.part,
        questionsLength: finalPart.questions?.length,
        textsLength: finalPart.texts?.length,
      });

      return finalPart;
    });

    // Filter out parts that have no questions at all (neither in part.questions nor in texts)
    const filteredParts = enhancedParts.filter((part) => {
      const hasPartQuestions = part.questions && part.questions.length > 0;
      const hasTextQuestions =
        part.texts &&
        part.texts.some((text) => text.questions && text.questions.length > 0);
      const shouldInclude = hasPartQuestions || hasTextQuestions;

      console.log(`\n🔍 Filter check for Part ${part.part}:`, {
        hasPartQuestions,
        hasTextQuestions,
        shouldInclude,
      });

      return shouldInclude;
    });

    console.log("\n✅ Final filteredParts count:", filteredParts.length);
    console.log(
      "✅ Filtered parts:",
      filteredParts.map((p) => ({
        part: p.part,
        questionsLength: p.questions?.length,
        textsLength: p.texts?.length,
      })),
    );

    readingData = {
      parts: filteredParts,
      oetReadingPart: filteredParts,
      discipline: test.discipline,
      test: test,
    };
  } else {
    readingData = {
      parts: test.parts || [],
      oetReadingPart: test.parts || [],
      discipline: test.discipline,
      test: test,
    };
  }

  return (
    <ReadingSectionReview
      readingData={readingData}
      answers={transformedAnswers}
      onExplainRequest={onReadingExplainRequest}
      loadingExplanations={readingLoadingExplanations}
      explanations={readingExplanations}
      moduleData={moduleData}
    />
  );
};

function AttemptResultPage() {
  const { testId, attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAttemptsUrl, getBackRoute } = useTestNavigation();

  // Check if we're in bundle context from location state
  const bundleId = location.state?.bundleId;
  const isBundle = location.state?.isBundle || !!bundleId;
  const testCategory = location.state?.testCategory;
  const module = location.state?.module;

  // Determine test type from URL path if not in state
  const getTestTypeFromPath = () => {
    const path = location.pathname.toLowerCase();
    if (path.includes("/mini-mock-tests/")) return "mini_mock";
    if (path.includes("/class-tests/")) return "class_test";
    return "full_mock";
  };

  const testType = testCategory || getTestTypeFromPath();

  const [activeTab, setActiveTab] = useState("overview");

  // Separate explanation states for each section
  const [listeningLoadingExplanations, setListeningLoadingExplanations] =
    useState({});
  const [listeningExplanations, setListeningExplanations] = useState({});
  const [readingLoadingExplanations, setReadingLoadingExplanations] = useState(
    {},
  );
  const [readingExplanations, setReadingExplanations] = useState({});

  const { data, isLoading, isError } =
    useGetTestAttemptAllResultsQuery(attemptId);

  // API transformResponse already extracts data.data, so we get { result: {...} }
  const result = data?.result;

  // Handler for AI explanation requests - Listening Section
  const handleListeningExplainRequest = useCallback(
    async (questionId, questionText, studentAnswer, correctAnswer) => {
      console.log("🎯 Listening handleExplainRequest called with:", {
        questionId,
        questionText: questionText?.substring(0, 100),
        studentAnswer,
        correctAnswer,
        alreadyLoading: listeningLoadingExplanations[questionId],
        hasExplanation: !!listeningExplanations[questionId],
      });

      // Skip if already loading or already have explanation
      if (
        listeningLoadingExplanations[questionId] ||
        listeningExplanations[questionId]
      ) {
        console.log(
          "⏭️ Listening - Skipping - already loading or has explanation",
        );
        return;
      }

      console.log(
        "⏳ Listening - Setting loading state for question",
        questionId,
      );
      setListeningLoadingExplanations((prev) => ({
        ...prev,
        [questionId]: true,
      }));

      try {
        console.log("🤖 Listening - Calling generateExplanationWithRetry...");
        // Use OpenAI API to generate explanation
        const explanation = await generateExplanationWithRetry(
          questionText,
          studentAnswer,
          correctAnswer,
        );

        console.log(
          "✅ Listening - Explanation generated successfully for question",
          questionId,
        );
        setListeningExplanations((prev) => ({
          ...prev,
          [questionId]: explanation,
        }));
      } catch (error) {
        console.error(
          "❌ Listening - Failed to get explanation for question",
          questionId,
          ":",
          error,
        );
        setListeningExplanations((prev) => ({
          ...prev,
          [questionId]: `Unable to generate explanation: ${error.message || "Please try again later."}`,
        }));
      } finally {
        console.log(
          "✅ Listening - Clearing loading state for question",
          questionId,
        );
        setListeningLoadingExplanations((prev) => ({
          ...prev,
          [questionId]: false,
        }));
      }
    },
    [listeningLoadingExplanations, listeningExplanations],
  );

  // Handler for AI explanation requests - Reading Section
  const handleReadingExplainRequest = useCallback(
    async (questionId, questionText, studentAnswer, correctAnswer) => {
      console.log("🎯 Reading handleExplainRequest called with:", {
        questionId,
        questionText: questionText?.substring(0, 100),
        studentAnswer,
        correctAnswer,
        alreadyLoading: readingLoadingExplanations[questionId],
        hasExplanation: !!readingExplanations[questionId],
      });

      // Skip if already loading or already have explanation
      if (
        readingLoadingExplanations[questionId] ||
        readingExplanations[questionId]
      ) {
        console.log(
          "⏭️ Reading - Skipping - already loading or has explanation",
        );
        return;
      }

      console.log(
        "⏳ Reading - Setting loading state for question",
        questionId,
      );
      setReadingLoadingExplanations((prev) => ({
        ...prev,
        [questionId]: true,
      }));

      try {
        console.log("🤖 Reading - Calling generateExplanationWithRetry...");
        // Use OpenAI API to generate explanation
        const explanation = await generateExplanationWithRetry(
          questionText,
          studentAnswer,
          correctAnswer,
        );

        console.log(
          "✅ Reading - Explanation generated successfully for question",
          questionId,
        );
        setReadingExplanations((prev) => ({
          ...prev,
          [questionId]: explanation,
        }));
      } catch (error) {
        console.error(
          "❌ Reading - Failed to get explanation for question",
          questionId,
          ":",
          error,
        );
        setReadingExplanations((prev) => ({
          ...prev,
          [questionId]: `Unable to generate explanation: ${error.message || "Please try again later."}`,
        }));
      } finally {
        console.log(
          "✅ Reading - Clearing loading state for question",
          questionId,
        );
        setReadingLoadingExplanations((prev) => ({
          ...prev,
          [questionId]: false,
        }));
      }
    },
    [readingLoadingExplanations, readingExplanations],
  );

  const handleBack = () => {
    if (isBundle && bundleId) {
      // Navigate back to bundle details page
      navigate(`/app/bundles/${bundleId}`);
    } else {
      // Navigate to test list page based on test type
      navigate(getBackRoute(testType));
    }
  };

  const getGradeColor = (grade) => {
    // Use brand color variations for all grades
    const colors = {
      "A+": "#166534",
      A: "#16a34a",
      "A-": "#22c55e",
      "B+": BRAND_PRIMARY,
      B: BRAND_PRIMARY,
      "B-": BRAND_LIGHT,
      "C+": BRAND_PRIMARY,
      C: BRAND_LIGHT,
      "C-": "#e57373",
      D: "#ef5350",
      E: "#e53935",
      F: "#c62828",
    };
    return colors[grade] || BRAND_PRIMARY;
  };

  const calculatePercentage = (obtained, total) => {
    if (!total) return 0;
    return Math.round((obtained / total) * 100);
  };

  const calculateScorePercentage = (moduleData) => {
    // For reading/listening: calculate based on correct answers out of attempted questions
    if (
      moduleData.totalAttemptedQuestion &&
      moduleData.correctCount !== undefined
    ) {
      return Math.round(
        (moduleData.correctCount / moduleData.totalAttemptedQuestion) * 100,
      );
    }
    // Fallback: calculate based on marks
    if (moduleData.obtainedMarks && moduleData.totalMarks) {
      return Math.round(
        (moduleData.obtainedMarks / moduleData.totalMarks) * 100,
      );
    }
    return 0;
  };

  const getAvailableModules = () => {
    if (!result) return [];
    return Object.entries(moduleConfig)
      .filter(
        ([_, config]) =>
          result[config.key] && Object.keys(result[config.key]).length > 0,
      )
      .map(([key, config]) => {
        // Create a copy to avoid mutation of frozen objects
        let moduleData = { ...result[config.key] };

        // Map avgBand to band for speaking module
        if (
          config.key === "speakingResult" &&
          moduleData.avgBand &&
          !moduleData.band
        ) {
          moduleData.band = moduleData.avgBand;
          moduleData.grade = moduleData.avgBand;
        }

        // Fix totalQuestions count for reading/listening modules
        // Only count questions that have a valid questionId (exclude null questionId)
        if (
          (config.key === "readingResult" ||
            config.key === "listeningResult") &&
          moduleData.answers
        ) {
          moduleData.totalQuestions = moduleData.answers.length;
        }

        // Calculate overall grade and status for writing module if needed
        if (config.key === "writingResult" && moduleData.writingTasks) {
          const tasks = moduleData.writingTasks;

          // Calculate overall grade if not present
          if (!moduleData.grade && !moduleData.band) {
            const taskGrades = tasks
              .map((task) => parseFloat(task.grade))
              .filter((grade) => !isNaN(grade));

            if (taskGrades.length > 0) {
              const avgGrade =
                taskGrades.reduce((sum, grade) => sum + grade, 0) /
                taskGrades.length;
              moduleData.band = (Math.round(avgGrade * 2) / 2).toFixed(1);
              moduleData.grade = moduleData.band;
            }
          }

          // Calculate overall status if not present
          if (!moduleData.checkingStatus) {
            const allChecked = tasks.every(
              (task) => task.checkingStatus === "checked",
            );
            const anyPending = tasks.some(
              (task) => task.checkingStatus === "pending",
            );
            moduleData.checkingStatus = allChecked
              ? "checked"
              : anyPending
                ? "pending"
                : "";
          }
        }

        return {
          ...config,
          id: key,
          data: moduleData,
        };
      });
  };

  const getTotalScore = () => {
    const modules = getAvailableModules();
    let totalObtained = 0;
    let totalMarks = 0;
    modules.forEach((module) => {
      totalObtained += module.data.obtainedMarks || 0;
      totalMarks += module.data.totalMarks || 0;
    });
    return { totalObtained, totalMarks };
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: `4px solid ${BRAND_LIGHT}`,
            borderTopColor: BRAND_PRIMARY,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ color: "#64748b", fontSize: "14px" }}>Loading results...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isError || !result) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: "24px", color: "#dc2626" }}>!</span>
        </div>
        <p style={{ color: "#dc2626", fontSize: "16px", fontWeight: "500" }}>
          Failed to load results
        </p>
        <button
          onClick={handleBack}
          style={{
            padding: "10px 20px",
            backgroundColor: BRAND_PRIMARY,
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const availableModules = getAvailableModules();
  const { totalObtained, totalMarks } = getTotalScore();
  const overallPercentage = calculatePercentage(totalObtained, totalMarks);

  return (
    <div style={{ padding: "8px", maxWidth: "100%", margin: "0" }}>
      {/* Compact Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
          borderRadius: "12px",
          padding: "12px 16px",
          marginBottom: "12px",
          color: "white",
          boxShadow: "0 4px 20px rgba(157, 34, 53, 0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Left side - Back button and title */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssessmentIcon style={{ fontSize: "20px" }} />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "700",
                  lineHeight: 1.2,
                }}
              >
                Test Results
              </h1>
              <p style={{ margin: 0, opacity: 0.85, fontSize: "11px" }}>
                Complete test review
              </p>
            </div>
          </div>

          {/* Right side - Score */}
          <div
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          ></div>
        </div>
      </div>

      {/* Module Score Cards - Compact */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        {availableModules.map((module) => {
          const Icon = module.icon;
          const percentage = calculatePercentage(
            module.data.obtainedMarks,
            module.data.totalMarks,
          );

          return (
            <div
              key={module.id}
              style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "14px 16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: `1px solid ${module.bgColor}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                borderLeft: `3px solid ${module.color}`,
              }}
              onClick={() => setActiveTab(module.id)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: module.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon style={{ fontSize: "18px", color: module.color }} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      {module.name}
                    </div>
                    {/* Only show band if status is not pending and band is greater than 0 */}
                    {!(
                      module.data.checkingStatus
                        ?.toLowerCase()
                        .includes("pending") ||
                      module.data.band == 0 ||
                      module.data.grade == 0
                    ) && (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "3px",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          backgroundColor:
                            getGradeColor(
                              module.data.band || module.data.grade,
                            ) + "20",
                          color: getGradeColor(
                            module.data.band || module.data.grade,
                          ),
                          fontSize: "10px",
                          fontWeight: "600",
                          marginTop: "2px",
                        }}
                      >
                        <GradeIcon style={{ fontSize: "11px" }} />
                        Band: {module.data.band || module.data.grade || "N/A"}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {module.key !== "writingResult" &&
                    module.key !== "speakingResult" && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#1e293b",
                        }}
                      >
                        {module.data.obtainedMarks || 0}
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            fontWeight: "400",
                          }}
                        >
                          /{module.data.totalMarks || 0}
                        </span>
                      </div>
                    )}
                  {module.key !== "writingResult" &&
                    module.key !== "speakingResult" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          justifyContent: "flex-end",
                          marginTop: "2px",
                        }}
                      ></div>
                    )}
                </div>
              </div>
              {/* Progress bar */}
              <div
                style={{
                  marginTop: "10px",
                  height: "4px",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percentage}%`,
                    height: "100%",
                    backgroundColor: module.color,
                    borderRadius: "2px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs Navigation */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "20px",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "0",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            padding: "12px 20px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            color: activeTab === "overview" ? BRAND_PRIMARY : "#64748b",
            borderBottom:
              activeTab === "overview"
                ? `2px solid ${BRAND_PRIMARY}`
                : "2px solid transparent",
            marginBottom: "-1px",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <TrendingUpIcon style={{ fontSize: "18px" }} />
          Overview
        </button>
        {availableModules.map((module) => {
          const Icon = module.icon;
          return (
            <button
              key={module.id}
              onClick={() => setActiveTab(module.id)}
              style={{
                padding: "12px 20px",
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                color: activeTab === module.id ? module.color : "#64748b",
                borderBottom:
                  activeTab === module.id
                    ? `2px solid ${module.color}`
                    : "2px solid transparent",
                marginBottom: "-1px",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Icon style={{ fontSize: "18px" }} />
              {module.name}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {activeTab === "overview" ? (
          <div style={{ padding: "24px" }}>
            <h3
              style={{
                margin: "0 0 20px 0",
                color: "#1e293b",
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              Performance Overview
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              {availableModules.map((module) => {
                const Icon = module.icon;

                return (
                  <div
                    key={module.id}
                    style={{
                      padding: "20px",
                      borderRadius: "10px",
                      backgroundColor: module.bgColor,
                      border: `1px solid ${module.color}30`,
                      cursor: "pointer",
                    }}
                    onClick={() => setActiveTab(module.id)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "12px",
                      }}
                    >
                      <Icon style={{ fontSize: "20px", color: module.color }} />
                      <span style={{ fontWeight: "600", color: "#1e293b" }}>
                        {module.name}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: "8px",
                        fontSize: "13px",
                      }}
                    >
                      {/* Only show band if status is not pending and band is greater than 0 */}
                      {!(
                        module.data.checkingStatus
                          ?.toLowerCase()
                          .includes("pending") ||
                        module.data.band == 0 ||
                        module.data.grade == 0
                      ) && (
                        <div>
                          <span style={{ color: "#64748b" }}>Band:</span>
                          <div
                            style={{
                              fontWeight: "600",
                              color: getGradeColor(
                                module.data.band || module.data.grade,
                              ),
                            }}
                          >
                            {module.data.band || module.data.grade || "N/A"}
                          </div>
                        </div>
                      )}
                      {/* Show Correct/Wrong/Attempted/Percentage for Listening and Reading */}
                      {module.key !== "writingResult" &&
                        module.key !== "speakingResult" && (
                          <>
                            <div>
                              <span style={{ color: "#64748b" }}>
                                Attempted:
                              </span>
                              <div
                                style={{ fontWeight: "600", color: "#1e293b" }}
                              >
                                {module.data.totalAttemptedQuestion || 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: "#64748b" }}>Total:</span>
                              <div
                                style={{ fontWeight: "600", color: "#1e293b" }}
                              >
                                {module.data.totalQuestions || 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: "#64748b" }}>Correct:</span>
                              <div
                                style={{ fontWeight: "600", color: "#10b981" }}
                              >
                                {module.data.correctCount || 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: "#64748b" }}>
                                Incorrect:
                              </span>
                              <div
                                style={{ fontWeight: "600", color: "#ef4444" }}
                              >
                                {module.data.wrongCount || 0}
                              </div>
                            </div>
                            {/* REMOVED: Score % section for IELTS tests */}
                          </>
                        )}
                      {/* Show Status for Writing and Speaking */}
                      {(module.key === "writingResult" ||
                        module.key === "speakingResult") && (
                        <div style={{ gridColumn: "span 2" }}>
                          <span style={{ color: "#64748b" }}>Status:</span>
                          <div
                            style={{
                              fontWeight: "600",
                              color:
                                module.data.checkingStatus === "checked" &&
                                (module.data.band > 0 || module.data.grade > 0)
                                  ? "#10b981"
                                  : module.data.checkingStatus === "pending" ||
                                      module.data.checkingStatus
                                        ?.toLowerCase()
                                        .includes("pending") ||
                                      module.data.band == 0 ||
                                      module.data.grade == 0
                                    ? "#f59e0b"
                                    : "#64748b",
                            }}
                          >
                            {module.data.checkingStatus === "checked" &&
                            (module.data.band > 0 || module.data.grade > 0)
                              ? "✓ Evaluated"
                              : module.data.checkingStatus === "pending" ||
                                  module.data.checkingStatus
                                    ?.toLowerCase()
                                    .includes("pending") ||
                                  module.data.band == 0 ||
                                  module.data.grade == 0
                                ? "⏳ Pending"
                                : module.data.checkingStatus || "N/A"}
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: "16px",
                        padding: "10px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        textAlign: "center",
                        fontSize: "13px",
                        color: module.color,
                        fontWeight: "500",
                      }}
                    >
                      Click to view full test
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick Answer Summary */}
            <div>
              <h4
                style={{
                  margin: "0 0 16px 0",
                  color: "#1e293b",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Quick Answer Summary
              </h4>
              {availableModules.map((module) => {
                const answers = module.data.answers || [];
                if (answers.length === 0) return null;

                return (
                  <div key={module.id} style={{ marginBottom: "20px" }}>
                    <h5
                      style={{
                        margin: "0 0 12px 0",
                        color: module.color,
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <module.icon style={{ fontSize: "18px" }} />
                      {module.name} Answers
                    </h5>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                      }}
                    >
                      {answers.map((answer, idx) => (
                        <div
                          key={idx}
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            fontWeight: "700",
                            backgroundColor: answer.isCorrect
                              ? "#dcfce7"
                              : "#fff",
                            color: answer.isCorrect ? "#166534" : "#991b1b",
                            border: `2px solid ${
                              answer.isCorrect ? "#16a34a" : "#dc2626"
                            }`,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                          title={`Q${answer.questionId}: ${
                            answer.isCorrect ? "Correct" : "Wrong"
                          }`}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                            e.currentTarget.style.backgroundColor =
                              answer.isCorrect ? "#16a34a" : "#dc2626";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.backgroundColor =
                              answer.isCorrect ? "#dcfce7" : "#fff";
                            e.currentTarget.style.color = answer.isCorrect
                              ? "#166534"
                              : "#991b1b";
                          }}
                        >
                          {answer.questionId}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Individual Module Detail View with Full Test
          (() => {
            const module = availableModules.find((m) => m.id === activeTab);
            if (!module) return null;

            const Icon = module.icon;

            return (
              <div>
                {/* Module Header */}
                <div
                  style={{
                    padding: "24px",
                    borderBottom: "1px solid #e2e8f0",
                    backgroundColor: module.bgColor,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    <Icon style={{ fontSize: "28px", color: module.color }} />
                    <h3
                      style={{ margin: 0, color: "#1e293b", fontSize: "20px" }}
                    >
                      {module.name} - Complete Test Review
                    </h3>
                  </div>

                  <div
                    style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}
                  >
                    {/* Only show Band if not pending and band > 0 */}
                    {!(
                      module.data.checkingStatus
                        ?.toLowerCase()
                        .includes("pending") ||
                      module.data.band == 0 ||
                      module.data.grade == 0
                    ) && (
                      <div
                        style={{
                          padding: "12px 20px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            marginBottom: "4px",
                          }}
                        >
                          Band
                        </div>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: getGradeColor(
                              module.data.band || module.data.grade,
                            ),
                          }}
                        >
                          {module.data.band || module.data.grade || "N/A"}
                        </div>
                      </div>
                    )}

                    {/* Only show Correct/Wrong/Attempted for Listening and Reading modules */}
                    {module.key !== "writingResult" &&
                      module.key !== "speakingResult" && (
                        <>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Attempted
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#64748b",
                              }}
                            >
                              {module.data.totalAttemptedQuestion || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Total
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#64748b",
                              }}
                            >
                              {module.data.totalQuestions || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Correct
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#10b981",
                              }}
                            >
                              {module.data.correctCount || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Incorrect
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#ef4444",
                              }}
                            >
                              {module.data.wrongCount || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Score
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: BRAND_PRIMARY,
                              }}
                            >
                              {module.data.obtainedMarks || 0}/
                              {module.data.totalMarks || 0}
                            </div>
                          </div>
                          {/* REMOVED: Score % section for IELTS tests */}
                        </>
                      )}

                    {/* Show Evaluation Status for Writing and Speaking modules */}
                    {(module.key === "writingResult" ||
                      module.key === "speakingResult") && (
                      <div
                        style={{
                          padding: "12px 20px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            marginBottom: "4px",
                          }}
                        >
                          Status
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color:
                              module.data.checkingStatus === "checked"
                                ? "#10b981"
                                : module.data.checkingStatus === "pending"
                                  ? "#f59e0b"
                                  : "#64748b",
                          }}
                        >
                          {module.data.checkingStatus === "checked"
                            ? "✓ Evaluated"
                            : module.data.checkingStatus === "pending"
                              ? "⏳ Pending"
                              : module.data.checkingStatus || "N/A"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Full Test Content */}
                <TestContentView
                  moduleData={module.data}
                  moduleConfig={module}
                  onListeningExplainRequest={handleListeningExplainRequest}
                  listeningLoadingExplanations={listeningLoadingExplanations}
                  listeningExplanations={listeningExplanations}
                  onReadingExplainRequest={handleReadingExplainRequest}
                  readingLoadingExplanations={readingLoadingExplanations}
                  readingExplanations={readingExplanations}
                />
              </div>
            );
          })()
        )}
      </div>

      {/* Print Styles */}
      <style>
        {`
                    @media print {
                        body { 
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        button { display: none !important; }
                        .no-print { display: none !important; }
                    }
                `}
      </style>
    </div>
  );
}

export default AttemptResultPage;
